import { useState, useEffect, useRef } from 'react';
import { getJobStatus, type JobStatusResponse } from '../lib/api';

interface UseJobPollingOptions {
  jobId: string | null;
  interval?: number; // milliseconds, default 3000 (3 seconds)
  maxAttempts?: number; // default 120 (6 minutes at 3s interval)
  onComplete?: (job: JobStatusResponse) => void;
  onError?: (error: Error) => void;
}

interface UseJobPollingResult {
  job: JobStatusResponse | null;
  isPolling: boolean;
  error: Error | null;
  stopPolling: () => void;
}

/**
 * Hook to poll job status until completion or failure
 *
 * @example
 * const { job, isPolling, error } = useJobPolling({
 *   jobId: 'uuid',
 *   interval: 3000,
 *   onComplete: (job) => {
 *     navigate(`/approve?postId=${job.resultPostId}`);
 *   },
 *   onError: (error) => {
 *     toast.error('Job failed: ' + error.message);
 *   }
 * });
 */
export function useJobPolling({
  jobId,
  interval = 3000,
  maxAttempts = 120,
  onComplete,
  onError,
}: UseJobPollingOptions): UseJobPollingResult {
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);
  const isMountedRef = useRef(true);

  // Stop polling function
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  };

  // Poll function
  const poll = async () => {
    if (!jobId || !isMountedRef.current) return;

    try {
      attemptsRef.current += 1;

      // Check max attempts
      if (attemptsRef.current > maxAttempts) {
        const timeoutError = new Error('Job polling timeout: max attempts reached');
        setError(timeoutError);
        stopPolling();
        onError?.(timeoutError);
        return;
      }

      // Fetch job status
      const jobStatus = await getJobStatus(jobId);

      if (!isMountedRef.current) return;

      setJob(jobStatus);

      // Check if job is complete
      if (jobStatus.status === 'completed') {
        stopPolling();
        onComplete?.(jobStatus);
      } else if (jobStatus.status === 'failed') {
        const failError = new Error(jobStatus.errorMessage || 'Job failed');
        setError(failError);
        stopPolling();
        onError?.(failError);
      }
      // Otherwise keep polling (status is 'pending' or 'processing')
    } catch (err) {
      if (!isMountedRef.current) return;

      const fetchError = err instanceof Error ? err : new Error('Failed to fetch job status');
      setError(fetchError);
      stopPolling();
      onError?.(fetchError);
    }
  };

  // Start polling when jobId changes
  useEffect(() => {
    if (!jobId) {
      stopPolling();
      return;
    }

    // Reset state
    setJob(null);
    setError(null);
    setIsPolling(true);
    attemptsRef.current = 0;

    // Initial poll
    poll();

    // Set up interval
    intervalRef.current = setInterval(poll, interval);

    // Cleanup
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, interval]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  return {
    job,
    isPolling,
    error,
    stopPolling,
  };
}
